/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * ChangHeon Yoo (chyoo@rsupport.com)
 *
 * @description
 *
 * @created at Mon May 27 2024
 **/
import { useMemo } from "react";
import { Grid, Row, TypographyTooltip, Typography } from "@/frameworks/designStuff/components";
import { _P } from "@rfice/language";
import { Maybe } from "@/frameworks/libraries/components";
import { hoverScrollCss } from "@/frameworks/designStuff/utils";

import { useFriendUser } from "@/frameworks/core/hooks";
import { Avatar } from "@/frameworks/core/components";
import { getFilteredUserListByValue } from "@/frameworks/core/utils";

import * as S from "./emotion";
import { colors } from "@/frameworks/core/styles";

export default function PhoneFriend({
	selectedUserList,
	searchValue,
	onUserSelected
}: {
	selectedUserList: WebServices.User.UserInfo[];
	searchValue: string;
	onUserSelected: (userID: string, name: string) => void;
}) {
	const {
		friends,
		handlers: { handleMatchData }
	} = useFriendUser();

	const searchedFriends = handleMatchData(getFilteredUserListByValue(friends ?? [], searchValue)) || [];

	const selectedUserMap = useMemo(() => {
		const map = new Map<string, number>();
		selectedUserList.forEach((user, index) => {
			map.set(user.userID, index + 1);
		});
		return map;
	}, [selectedUserList]);

	const getBackgroundColor = (userId: string) => {
		const position = selectedUserMap.get(userId);
		if (position === 2 || position === 3) return colors.gradient_40_100_w60;
		return undefined;
	};

	return (
		<div css={[{ height: "284px", overflow: "auto" }, hoverScrollCss()]}>
			<Maybe
				test={Boolean(searchedFriends?.length > 0)}
				fallback={
					<S.EmptySearch>
						<Typography.p typoSize="Body3" align="center">
							{_P("검색 결과가 없습니다.<br />다른 검색어를 입력해주세요.")}
						</Typography.p>
					</S.EmptySearch>
				}
			>
				<Grid.ul column={4} gridGap={2} style={{ placeItems: "center" }}>
					{searchedFriends?.map((friend) => {
						const backgroundColor = getBackgroundColor(friend.userId);

						return (
							<S.GridUserItemStyled
								backgroundColor={backgroundColor}
								key={friend.userId}
								horizonAlign="center"
								onClick={() => onUserSelected(friend.userId, friend.name)}
							>
								<Avatar id={friend.userId} kind={"people_48"} showMiniProfile={false} />
								<Row horizonAlign="center" css={{ width: "100%", overflow: "hidden" }}>
									<TypographyTooltip.p
										tooltipProps={{ hasIcon: false }}
										componentProps={{
											ellipsis: true,
											maxLine: 1,
											typoColor: "neutral_70_100",
											align: "center",
											typoSize: "Body6"
										}}
									>
										{friend.name}
									</TypographyTooltip.p>
								</Row>
							</S.GridUserItemStyled>
						);
					})}
				</Grid.ul>
			</Maybe>
		</div>
	);
}
